import React, { useState } from 'react';
import {
  ArrowLeft,
  Search,
  ChevronDown,
  FileSpreadsheet,
  FileText,
  Printer,
  Download,
  X,
  Building,
  User,
  Check,
} from 'lucide-react';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from 'recharts';
import { ResidentSubMenuKey } from '../ResidentDirectory';

interface Props {
  onBack: () => void;
  onNavigate?: (tab: ResidentSubMenuKey) => void;
}

export interface ForeignOwnershipRecord {
  id: string;
  orderNo: number;
  roomNumber: string; // e.g. "1086/01"
  unitDeedNumber: string; // e.g. "-"
  owners: { name: string; nationality: string }[];
  unitType: string; // e.g. "บ้านเดี่ยว", "3 ห้องนอน", "สตูดิโอ", "คอนโด", "อาคารพาณิชย์", "1 ห้องนอน", "2 ห้องนอน"
  areaSqm: number; // e.g. 125.30
  ratioPct: number; // e.g. 0.90
  quotaRatio: number | null; // e.g. 1408.70, null, 100.00, 27.68
  floorsCount: number; // e.g. 18, 7, 8, 5, 1, 2
  status: 'ปล่อยเช่า' | 'อยู่อาศัยเอง';
  residentsCount: number;
}

// Exactly matching images 5, 6, 7, 8
const INITIAL_FOREIGN_RECORDS: ForeignOwnershipRecord[] = [
  {
    id: 'fo-1',
    orderNo: 1,
    roomNumber: '1086/01',
    unitDeedNumber: '-',
    owners: [{ name: 'Mr.adisorn kaeophakdee', nationality: 'AF' }],
    unitType: 'บ้านเดี่ยว',
    areaSqm: 125.3,
    ratioPct: 0.9,
    quotaRatio: 1408.7,
    floorsCount: 18,
    status: 'ปล่อยเช่า',
    residentsCount: 12,
  },
  {
    id: 'fo-2',
    orderNo: 2,
    roomNumber: '1086/07',
    unitDeedNumber: '-',
    owners: [
      { name: 'MissLaura Anderson', nationality: 'TH' },
      { name: 'Mr.adisorn kaeophakdee', nationality: 'AF' },
    ],
    unitType: '3 ห้องนอน',
    areaSqm: 130.72,
    ratioPct: 0.94,
    quotaRatio: null,
    floorsCount: 7,
    status: 'อยู่อาศัยเอง',
    residentsCount: 2,
  },
  {
    id: 'fo-3',
    orderNo: 3,
    roomNumber: '1086/12',
    unitDeedNumber: '-',
    owners: [
      { name: 'Mr.adisorn kaeophakdee', nationality: 'AF' },
      { name: 'นางพรรณทิภา อัครเดชธนิกุล', nationality: 'TH' },
    ],
    unitType: 'สตูดิโอ',
    areaSqm: 46.32,
    ratioPct: 0.33,
    quotaRatio: null,
    floorsCount: 8,
    status: 'อยู่อาศัยเอง',
    residentsCount: 4,
  },
  {
    id: 'fo-4',
    orderNo: 4,
    roomNumber: '1086/135',
    unitDeedNumber: '-',
    owners: [{ name: 'Mr.John Chen', nationality: 'SG' }],
    unitType: '2 ห้องนอน',
    areaSqm: 110.0,
    ratioPct: 0.79,
    quotaRatio: null,
    floorsCount: 8,
    status: 'อยู่อาศัยเอง',
    residentsCount: 1,
  },
  {
    id: 'fo-5',
    orderNo: 5,
    roomNumber: '1806/01',
    unitDeedNumber: '-',
    owners: [{ name: 'Mr.Mario dejung', nationality: 'RS' }],
    unitType: '2 ห้องนอน',
    areaSqm: 13.0,
    ratioPct: 0.09,
    quotaRatio: null,
    floorsCount: 5,
    status: 'อยู่อาศัยเอง',
    residentsCount: 1,
  },
  {
    id: 'fo-6',
    orderNo: 6,
    roomNumber: '111/1',
    unitDeedNumber: '-',
    owners: [{ name: 'Mr.adisorn kaeophakdee', nationality: 'AF' }],
    unitType: 'คอนโด',
    areaSqm: 123.8,
    ratioPct: 0.89,
    quotaRatio: 100.0,
    floorsCount: 5,
    status: 'อยู่อาศัยเอง',
    residentsCount: 2,
  },
  {
    id: 'fo-7',
    orderNo: 7,
    roomNumber: '1122',
    unitDeedNumber: '-',
    owners: [
      { name: 'นางสาวtt rr', nationality: 'TG' },
      { name: 'คุณ11 22', nationality: 'TH' },
    ],
    unitType: 'อาคารพาณิชย์',
    areaSqm: 599.0,
    ratioPct: 4.32,
    quotaRatio: null,
    floorsCount: 1,
    status: 'อยู่อาศัยเอง',
    residentsCount: 2,
  },
  {
    id: 'fo-8',
    orderNo: 8,
    roomNumber: '999/999',
    unitDeedNumber: '-',
    owners: [{ name: 'คุณpang pangg', nationality: 'EC' }],
    unitType: '1 ห้องนอน',
    areaSqm: 27.68,
    ratioPct: 0.2,
    quotaRatio: 27.68,
    floorsCount: 2,
    status: 'อยู่อาศัยเอง',
    residentsCount: 1,
  },
  {
    id: 'fo-9',
    orderNo: 9,
    roomNumber: '1788/2',
    unitDeedNumber: '-',
    owners: [
      { name: 'Mr.adisorn kaeophakdee', nationality: 'AF' },
      { name: 'คุณadisorn kpd', nationality: 'TH' },
    ],
    unitType: 'คอนโด',
    areaSqm: 100.0,
    ratioPct: 0.72,
    quotaRatio: null,
    floorsCount: 8,
    status: 'อยู่อาศัยเอง',
    residentsCount: 3,
  },
];

export const ResidentForeignOwnershipView: React.FC<Props> = ({ onBack, onNavigate }) => {
  const [records] = useState<ForeignOwnershipRecord[]>(INITIAL_FOREIGN_RECORDS);
  const [searchQuery, setSearchQuery] = useState('');
  const [itemsPerPage, setItemsPerPage] = useState('10');
  const [showPrintMenu, setShowPrintMenu] = useState(false);
  const [selectedItem, setSelectedItem] = useState<ForeignOwnershipRecord | null>(null);
  const [exportNotice, setExportNotice] = useState<string | null>(null);

  const tabs: { key: ResidentSubMenuKey; label: string }[] = [
    { key: 'members', label: 'เจ้าของร่วม/สมาชิก' },
    { key: 'vehicles', label: 'ยานพาหนะ' },
    { key: 'keycards', label: 'คีย์การ์ด' },
    { key: 'foreign_ownership', label: 'กรรมสิทธิ์ชาวต่างชาติ' },
    { key: 'pets', label: 'สัตว์เลี้ยง' },
    { key: 'title_deeds', label: 'กรรมสิทธิ์' },
    { key: 'history_logs', label: 'ประวัติผู้อยู่อาศัย' },
  ];

  // Pie 1: Thai vs Foreign ratio
  const thaiVsForeignData = [
    { name: 'คนไทย', value: 91.74, color: '#0d5cb6' },
    { name: 'ชาวต่างชาติ', value: 8.26, color: '#0284c7' },
  ];

  // Pie 2: Foreign nationality breakdown
  const countryBreakdownData = [
    { name: 'Afghan(AF)', value: 55.56, color: '#0d5cb6' },
    { name: 'Singaporean(SG)', value: 11.11, color: '#0284c7' },
    { name: 'Serbian(RS)', value: 11.11, color: '#06b6d4' },
    { name: 'Togolese(TG)', value: 11.11, color: '#2dd4bf' },
    { name: 'Other', value: 11.11, color: '#e0f2fe' },
  ];

  const filteredRecords = records.filter((r) => {
    const q = searchQuery.toLowerCase().trim();
    if (!q) return true;
    const matchRoom = r.roomNumber.toLowerCase().includes(q);
    const matchUnitType = r.unitType.toLowerCase().includes(q);
    const matchStatus = r.status.toLowerCase().includes(q);
    const matchOwners = r.owners.some(
      (o) => o.name.toLowerCase().includes(q) || o.nationality.toLowerCase().includes(q)
    );
    return matchRoom || matchUnitType || matchStatus || matchOwners;
  });

  // Calculate totals
  const totalArea = filteredRecords.reduce((sum, r) => sum + r.areaSqm, 0);
  const totalRatio = filteredRecords.reduce((sum, r) => sum + r.ratioPct, 0);
  const totalQuota = filteredRecords.reduce((sum, r) => sum + (r.quotaRatio || 0), 0);

  const handleExport = (type: 'Excel' | 'PDF') => {
    setShowPrintMenu(false);
    setExportNotice(`กำลังดาวน์โหลดรายงานกรรมสิทธิ์ชาวต่างชาติ (${type})...`);
    setTimeout(() => {
      setExportNotice(null);
    }, 2500);
  };

  return (
    <div className="font-prompt bg-white min-h-[85vh] rounded-2xl shadow-xs border border-slate-200/80 overflow-hidden">
      {/* 1. Top Sub-Navigation Tabs */}
      <div className="border-b border-slate-200 bg-slate-50/50 px-4 sm:px-6 pt-3 overflow-x-auto">
        <div className="flex items-center gap-6 sm:gap-8 min-w-max text-xs sm:text-sm">
          {tabs.map((tab) => {
            const isActive = tab.key === 'foreign_ownership';
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => {
                  if (tab.key === 'foreign_ownership') return;
                  if (onNavigate) {
                    onNavigate(tab.key);
                  } else {
                    onBack();
                  }
                }}
                className={`pb-3 font-medium transition-colors cursor-pointer relative ${
                  isActive
                    ? 'text-slate-900 font-bold border-b-2 border-teal-600'
                    : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="p-4 sm:p-6 space-y-6">
        {/* 2. Top Header with Back Button & Print Document Dropdown */}
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onBack}
              className="w-7 h-7 rounded-full border border-slate-300 hover:border-slate-400 bg-white flex items-center justify-center text-slate-600 hover:text-slate-900 transition-colors cursor-pointer"
              title="ย้อนกลับ"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <h1 className="text-lg sm:text-xl font-bold text-slate-800 tracking-tight">
              กรรมสิทธิ์ชาวต่างชาติ
            </h1>
          </div>

          {/* Right Print Button with Dropdown */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowPrintMenu(!showPrintMenu)}
              className="px-3.5 py-1.5 border border-sky-600 text-sky-700 hover:bg-sky-50 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-colors cursor-pointer"
            >
              พิมพ์เอกสาร <ChevronDown className="w-3.5 h-3.5 text-sky-700" />
            </button>

            {showPrintMenu && (
              <div className="absolute right-0 mt-1 w-32 bg-white border border-slate-200 rounded-xl shadow-lg z-30 py-1 text-xs">
                <button
                  type="button"
                  onClick={() => handleExport('Excel')}
                  className="w-full px-3 py-2 text-left text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                >
                  <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-600" /> Excel
                </button>
                <button
                  type="button"
                  onClick={() => handleExport('PDF')}
                  className="w-full px-3 py-2 text-left text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                >
                  <FileText className="w-3.5 h-3.5 text-rose-600" /> PDF
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Export Notification */}
        {exportNotice && (
          <div className="bg-emerald-50 text-emerald-700 border border-emerald-200 px-4 py-2 rounded-xl text-xs flex items-center gap-2 animate-in fade-in">
            <Check className="w-4 h-4" /> {exportNotice}
          </div>
        )}

        {/* 3. Two Side-by-Side Pie Chart Cards matching images 5, 7, 8 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Card 1: อัตราส่วนกรรมสิทธิ์ชาวต่างชาติและคนไทย (หน่วย: หลัง) */}
          <div className="bg-white rounded-2xl border border-slate-200/90 p-5 shadow-2xs flex flex-col justify-between">
            <h3 className="text-center text-xs sm:text-sm font-bold text-slate-800 mb-4">
              อัตราส่วนกรรมสิทธิ์ชาวต่างชาติและคนไทย (หน่วย: หลัง)
            </h3>

            <div className="h-56 w-full flex items-center justify-center">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Tooltip
                    formatter={(value: number | undefined) => [
                      `${value !== undefined ? value.toFixed(2) : 0}%`,
                      'สัดส่วน',
                    ]}
                    contentStyle={{
                      backgroundColor: '#fff',
                      borderRadius: '8px',
                      fontSize: '12px',
                      border: '1px solid #e2e8f0',
                    }}
                  />
                  <Pie
                    data={thaiVsForeignData}
                    cx="50%"
                    cy="50%"
                    innerRadius={0}
                    outerRadius={85}
                    dataKey="value"
                    startAngle={90}
                    endAngle={-270}
                  >
                    {thaiVsForeignData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} stroke="#ffffff" strokeWidth={1.5} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Custom Legend */}
            <div className="flex flex-wrap items-center justify-center gap-6 mt-4 pt-3 border-t border-slate-100 text-xs text-slate-700">
              {thaiVsForeignData.map((item) => (
                <div key={item.name} className="flex items-center gap-2">
                  <span
                    className="w-3.5 h-3.5 rounded-xs inline-block"
                    style={{ backgroundColor: item.color }}
                  />
                  <span className="font-medium text-slate-800">
                    {item.name} {item.value.toFixed(2)}%
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Card 2: อัตราส่วนกรรมสิทธิ์ชาวต่างชาติแต่ละประเทศ (หน่วย: หลัง) */}
          <div className="bg-white rounded-2xl border border-slate-200/90 p-5 shadow-2xs flex flex-col justify-between">
            <h3 className="text-center text-xs sm:text-sm font-bold text-slate-800 mb-4">
              อัตราส่วนกรรมสิทธิ์ชาวต่างชาติแต่ละประเทศ (หน่วย: หลัง)
            </h3>

            <div className="h-56 w-full flex items-center justify-center">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Tooltip
                    formatter={(value: number | undefined) => [
                      `${value !== undefined ? value.toFixed(2) : 0}%`,
                      'สัดส่วน',
                    ]}
                    contentStyle={{
                      backgroundColor: '#fff',
                      borderRadius: '8px',
                      fontSize: '12px',
                      border: '1px solid #e2e8f0',
                    }}
                  />
                  <Pie
                    data={countryBreakdownData}
                    cx="50%"
                    cy="50%"
                    innerRadius={0}
                    outerRadius={85}
                    dataKey="value"
                    startAngle={90}
                    endAngle={-270}
                  >
                    {countryBreakdownData.map((entry, index) => (
                      <Cell key={`cell-country-${index}`} fill={entry.color} stroke="#ffffff" strokeWidth={1.5} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Custom Legend */}
            <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 mt-4 pt-3 border-t border-slate-100 text-xs text-slate-700">
              {countryBreakdownData.map((item) => (
                <div key={item.name} className="flex items-center gap-1.5">
                  <span
                    className="w-3 h-3 rounded-xs inline-block shrink-0"
                    style={{ backgroundColor: item.color, border: item.color === '#e0f2fe' ? '1px solid #cbd5e1' : 'none' }}
                  />
                  <span className="text-[11px] text-slate-700 whitespace-nowrap">
                    {item.name} {item.value.toFixed(2)}%
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* 4. Search Filter Bar on Top Right */}
        <div className="flex justify-end pt-2">
          <div className="relative w-full sm:w-64">
            <input
              type="text"
              placeholder="ค้นหา"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-3 pr-8 py-1.5 bg-white border border-slate-300 rounded-md text-xs text-slate-700 focus:outline-none focus:ring-1 focus:ring-sky-500 w-full placeholder-slate-400"
            />
            <Search className="w-3.5 h-3.5 text-slate-400 absolute right-2.5 top-1/2 -translate-y-1/2" />
          </div>
        </div>

        {/* 5. Table matching images 6 & 7 */}
        <div className="border border-slate-200 rounded-xl overflow-hidden shadow-2xs">
          <div className="overflow-x-auto">
            <table className="w-full text-xs text-left border-collapse">
              <thead>
                <tr className="bg-slate-100/90 text-slate-700 font-medium border-b border-slate-200">
                  <th className="py-2.5 px-3 text-center whitespace-nowrap">ลำดับ</th>
                  <th className="py-2.5 px-3 text-left whitespace-nowrap">ห้อง</th>
                  <th className="py-2.5 px-3 text-center whitespace-nowrap">ห้องชุดเลขที่</th>
                  <th className="py-2.5 px-4 text-left whitespace-nowrap">เจ้าของกรรมสิทธิ์</th>
                  <th className="py-2.5 px-3 text-center whitespace-nowrap">สัญชาติ</th>
                  <th className="py-2.5 px-3 text-left whitespace-nowrap">ประเภท</th>
                  <th className="py-2.5 px-3 text-right whitespace-nowrap">พื้นที่</th>
                  <th className="py-2.5 px-3 text-right whitespace-nowrap">%</th>
                  <th className="py-2.5 px-3 text-right whitespace-nowrap">อัตราส่วนกรรมสิทธิ์</th>
                  <th className="py-2.5 px-3 text-center whitespace-nowrap">จำนวนชั้น</th>
                  <th className="py-2.5 px-3 text-left whitespace-nowrap">สถานะ</th>
                  <th className="py-2.5 px-3 text-center whitespace-nowrap">จำนวนผู้อยู่อาศัย</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-slate-700">
                {filteredRecords.map((rec) => (
                  <tr key={rec.id} className="hover:bg-slate-50/80 transition-colors">
                    {/* ลำดับ */}
                    <td className="py-3 px-3 text-center text-slate-600">{rec.orderNo}</td>

                    {/* ห้อง */}
                    <td className="py-3 px-3 text-left font-medium">
                      <button
                        type="button"
                        onClick={() => setSelectedItem(rec)}
                        className="text-sky-600 hover:text-sky-800 hover:underline cursor-pointer"
                      >
                        {rec.roomNumber}
                      </button>
                    </td>

                    {/* ห้องชุดเลขที่ */}
                    <td className="py-3 px-3 text-center text-slate-400">{rec.unitDeedNumber}</td>

                    {/* เจ้าของกรรมสิทธิ์ */}
                    <td className="py-3 px-4 text-left text-slate-800">
                      <div className="space-y-0.5">
                        {rec.owners.map((owner, idx) => (
                          <div key={idx}>{owner.name}</div>
                        ))}
                      </div>
                    </td>

                    {/* สัญชาติ */}
                    <td className="py-3 px-3 text-center font-medium text-slate-700">
                      <div className="space-y-0.5">
                        {rec.owners.map((owner, idx) => (
                          <div key={idx}>{owner.nationality}</div>
                        ))}
                      </div>
                    </td>

                    {/* ประเภท */}
                    <td className="py-3 px-3 text-left text-slate-700 whitespace-nowrap">{rec.unitType}</td>

                    {/* พื้นที่ */}
                    <td className="py-3 px-3 text-right font-mono text-slate-800">
                      {rec.areaSqm.toFixed(2)}
                    </td>

                    {/* % */}
                    <td className="py-3 px-3 text-right font-mono text-slate-800">
                      {rec.ratioPct.toFixed(2)}%
                    </td>

                    {/* อัตราส่วนกรรมสิทธิ์ */}
                    <td className="py-3 px-3 text-right font-mono text-slate-800">
                      {rec.quotaRatio !== null
                        ? rec.quotaRatio.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                        : '-'}
                    </td>

                    {/* จำนวนชั้น */}
                    <td className="py-3 px-3 text-center text-slate-700">{rec.floorsCount}</td>

                    {/* สถานะ */}
                    <td className="py-3 px-3 text-left text-slate-700 whitespace-nowrap">{rec.status}</td>

                    {/* จำนวนผู้อยู่อาศัย */}
                    <td className="py-3 px-3 text-center text-slate-800 font-medium">
                      {rec.residentsCount}
                    </td>
                  </tr>
                ))}

                {/* Summary Row matching screenshots */}
                {filteredRecords.length > 0 && (
                  <tr className="bg-slate-50/90 font-bold text-slate-900 border-t border-slate-200">
                    <td colSpan={5} className="py-3 px-4 text-center">
                      รวม
                    </td>
                    <td className="py-3 px-3 text-left"></td>
                    <td className="py-3 px-3 text-right font-mono">
                      {totalArea.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="py-3 px-3 text-right font-mono">
                      {totalRatio.toFixed(2)}%
                    </td>
                    <td className="py-3 px-3 text-right font-mono">
                      {totalQuota.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td colSpan={3}></td>
                  </tr>
                )}

                {filteredRecords.length === 0 && (
                  <tr>
                    <td colSpan={12} className="py-8 text-center text-slate-400">
                      ไม่พบข้อมูลกรรมสิทธิ์ชาวต่างชาติที่ค้นหา
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Table Footer */}
          <div className="bg-white px-4 py-3 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-500">
            <div className="flex items-center gap-2">
              <span>แสดง</span>
              <div className="relative">
                <select
                  value={itemsPerPage}
                  onChange={(e) => setItemsPerPage(e.target.value)}
                  className="appearance-none pl-2.5 pr-6 py-1 bg-white border border-slate-300 rounded text-xs text-slate-700 focus:outline-none focus:ring-1 focus:ring-sky-500 cursor-pointer"
                >
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
                <ChevronDown className="w-3 h-3 text-slate-400 absolute right-1.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              </div>
              <span>รายการ/หน้า</span>
            </div>

            <div className="text-slate-600">
              รายการทั้งหมด {filteredRecords.length}
            </div>
          </div>
        </div>
      </div>

      {/* Modal: View Details */}
      {selectedItem && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-lg rounded-2xl shadow-xl border border-slate-200 overflow-hidden animate-in fade-in zoom-in-95 duration-150">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-slate-50/50">
              <div className="flex items-center gap-2">
                <Building className="w-5 h-5 text-sky-600" />
                <h3 className="font-bold text-slate-800">
                  รายละเอียดกรรมสิทธิ์ห้อง {selectedItem.roomNumber}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setSelectedItem(null)}
                className="w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-6 space-y-4 text-xs text-slate-700">
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 space-y-2">
                <span className="text-slate-400 font-semibold block mb-1">รายชื่อเจ้าของกรรมสิทธิ์</span>
                {selectedItem.owners.map((owner, idx) => (
                  <div key={idx} className="flex items-center justify-between py-1 border-b border-slate-200/50 last:border-0">
                    <span className="font-medium text-slate-900 text-sm flex items-center gap-1.5">
                      <User className="w-3.5 h-3.5 text-slate-400" /> {owner.name}
                    </span>
                    <span className="px-2 py-0.5 rounded bg-sky-100 text-sky-800 font-bold">
                      สัญชาติ {owner.nationality}
                    </span>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="bg-slate-50 p-3 rounded-xl border border-slate-100">
                  <span className="text-slate-400 block mb-0.5">ประเภทห้องชุด</span>
                  <span className="font-bold text-slate-800">{selectedItem.unitType}</span>
                </div>
                <div className="bg-slate-50 p-3 rounded-xl border border-slate-100">
                  <span className="text-slate-400 block mb-0.5">ขนาดพื้นที่</span>
                  <span className="font-bold text-slate-800 font-mono">{selectedItem.areaSqm} ตร.ม.</span>
                </div>
                <div className="bg-slate-50 p-3 rounded-xl border border-slate-100">
                  <span className="text-slate-400 block mb-0.5">สัดส่วน (%)</span>
                  <span className="font-bold text-slate-800 font-mono">{selectedItem.ratioPct}%</span>
                </div>
                <div className="bg-slate-50 p-3 rounded-xl border border-slate-100">
                  <span className="text-slate-400 block mb-0.5">สถานะการใช้งาน</span>
                  <span className="font-bold text-slate-800">{selectedItem.status}</span>
                </div>
              </div>
            </div>

            <div className="px-6 py-3 bg-slate-50 border-t border-slate-100 flex justify-end">
              <button
                type="button"
                onClick={() => setSelectedItem(null)}
                className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-xl font-medium text-xs transition-colors cursor-pointer"
              >
                ปิดหน้าต่าง
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
